import { useCallback, useMemo, useState } from "react";
import {
    Box,
    Paper,
    Skeleton,
    Stack,
    Typography,
} from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import InvestmentsBalanceCard from "features/investments/components/InvestmentsBalanceCard";
import AddOperationCard from "features/investments/components/AddOperationCard";
import type { AddOperationInitialValues } from "features/investments/components/AddOperationCard";
import FloatingActionButton from "shared/ui/FloatingActionButton";
import PanelSectionLink from "app/components/layout/panel/PanelSectionLink";
import BalanceDisplay from "shared/money/BalanceDisplay";
import { useUserSession } from "shared/contexts/UserContext";
import { useMarketQuotesContext } from "features/market";
import { usePortfolio } from "features/investments/hooks/usePortfolio";
import { useInstrumentEvents } from "features/market";
import { useTranslation } from "shared/hooks/useTranslation";
import { buildPortfolioGuideItems } from "lib/guideBuilders";
import { getActionableInstrumentEvents, getSuggestedAmortizationPrice, getSuggestedAmortizationQuantity } from "features/market";
import { addTransaction as addBudgetTransaction } from "features/budget";
import { buildBudgetTransactionFromPortfolioOperation } from "features/investments/lib/portfolioOperationWallet";
import type { AddOperationPayload, PositionWithAsset } from "types/Portfolio";
import type { AppNotification } from "types/Notification";
import type { PositionInstrumentEvent } from "features/market";
import type { PortfolioPanelFilter } from "features/market";

function positionValue(position: PositionWithAsset): number {
    const price = position.currentPrice ?? position.avgPrice;
    return position.quantity * price;
}

function positionPnlPct(position: PositionWithAsset): number | null {
    if (position.currentPrice == null || !(position.avgPrice > 0)) return null;
    return ((position.currentPrice - position.avgPrice) / position.avgPrice) * 100;
}

function isPortfolioPanelFilter(value: string | undefined): value is PortfolioPanelFilter {
    return value === "acciones" || value === "bonos" || value === "letras" || value === "cedears" || value === "on" || value === "crypto";
}

function buildAmortizationInitialValues(match: PositionInstrumentEvent): AddOperationInitialValues {
    const asset = match.position.assetId;
    const marketPanelRaw = asset?.marketPanel;
    const marketPanel = isPortfolioPanelFilter(marketPanelRaw) ? marketPanelRaw : "bonos";
    return {
        side: "amortization",
        type: asset?.type === "custom" ? "custom" : "market",
        symbol: asset?.symbol ?? match.event.ticker,
        name: asset?.name ?? match.event.ticker,
        marketPanel,
        quantity: getSuggestedAmortizationQuantity(match.event, match.position),
        price: getSuggestedAmortizationPrice(match.event, match.position),
        date: match.event.date,
        currency: match.event.currency || match.position.currency || "ARS",
    };
}

export default function PortfolioPanelPane() {
    const { t, currentLanguage, getPathWithLanguage } = useTranslation();
    const navigate = useNavigate();
    const { profile } = useUserSession();
    const username = profile?.username ?? "";
    const { quoteCurrencyView } = useMarketQuotesContext();

    const {
        positions,
        operations,
        loading,
        error,
        refetch,
        addOperation,
    } = usePortfolio(username || null);
    const { events: instrumentEvents, loading: instrumentEventsLoading } = useInstrumentEvents(Boolean(username));

    const [operationDialogOpen, setOperationDialogOpen] = useState(false);
    const [operationInitialValues, setOperationInitialValues] = useState<AddOperationInitialValues | null>(null);

    const totalPortfolioValue = useMemo(
        () => positions.reduce((sum, p) => sum + positionValue(p), 0),
        [positions],
    );

    const topPositions = useMemo(
        () => [...positions]
            .sort((a, b) => positionValue(b) - positionValue(a))
            .slice(0, 5),
        [positions],
    );

    const portfolioGuideItems = useMemo<AppNotification[]>(
        () => buildPortfolioGuideItems({
            positions,
            operations,
            instrumentEvents,
            totalValue: totalPortfolioValue,
            quoteCurrency: quoteCurrencyView,
            t,
            language: currentLanguage,
            username,
        }),
        [currentLanguage, instrumentEvents, operations, positions, quoteCurrencyView, t, totalPortfolioValue, username],
    );

    const actionableInstrumentEvents = useMemo(
        () => getActionableInstrumentEvents({
            positions,
            operations,
            events: instrumentEvents,
            upcomingDays: 45,
        }),
        [instrumentEvents, operations, positions],
    );

    const openOperationDialog = useCallback((initialValues: AddOperationInitialValues | null = null) => {
        setOperationInitialValues(initialValues);
        setOperationDialogOpen(true);
    }, []);

    const handleGuideItemClick = useCallback((item: AppNotification) => {
        if (item.sourceId?.startsWith("instrument-event:")) {
            const eventId = item.sourceId.replace("instrument-event:", "");
            const match = actionableInstrumentEvents.find(({ event }) => event.id === eventId);
            if (match) {
                openOperationDialog(buildAmortizationInitialValues(match));
                return;
            }
        }
        if (item.deepLink) {
            navigate(item.deepLink.startsWith("/") ? item.deepLink : `/${item.deepLink}`);
        }
    }, [actionableInstrumentEvents, navigate, openOperationDialog]);

    const handleCreateWalletTransaction = useCallback(
        async (payload: AddOperationPayload, liquidityPoolId: string) => {
            if (!username || !liquidityPoolId) {
                return { success: false, error: t("common.error") };
            }
            const tx = buildBudgetTransactionFromPortfolioOperation({
                username,
                payload,
                liquidityPoolId,
            });
            if (!tx) return { success: true };
            try {
                await addBudgetTransaction(username, tx, currentLanguage);
                return { success: true };
            } catch (error) {
                return {
                    success: false,
                    error: error instanceof Error ? error.message : t("portfolio.walletSyncError"),
                };
            }
        },
        [username, t, currentLanguage],
    );

    const investmentsPath = username ? getPathWithLanguage(`${username}/investments`) : "#";

    return (
        <Stack spacing={2} sx={{ pb: 7 }}>
            <InvestmentsBalanceCard
                density="panel"
                totalValue={totalPortfolioValue}
                displayCurrency={quoteCurrencyView}
                loading={loading}
                positions={positions}
                operations={operations}
            />

            <Paper
                elevation={0}
                sx={{
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    overflow: "hidden",
                }}
            >
                <Box sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: "divider" }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 850 }}>
                        {t("secondaryPanel.topPositions")}
                    </Typography>
                </Box>
                {loading ? (
                    <Stack spacing={1} sx={{ p: 1.5 }}>
                        {[0, 1, 2].map((i) => (
                            <Skeleton key={i} height={36} />
                        ))}
                    </Stack>
                ) : error ? (
                    <Typography variant="body2" color="error" sx={{ p: 1.5 }}>
                        {error}
                    </Typography>
                ) : topPositions.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 1.5 }}>
                        {t("portfolio.noPositionsTitle")}
                    </Typography>
                ) : (
                    topPositions.map((position, index) => {
                        const symbol = position.assetId?.symbol ?? position.assetId?.name ?? "-";
                        const pnl = positionPnlPct(position);
                        return (
                            <Box
                                key={`${symbol}-${position.assetId?.marketPanel ?? "m"}-${index}`}
                                component={RouterLink}
                                to={investmentsPath}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 1,
                                    px: 1.5,
                                    py: 0.85,
                                    textDecoration: "none",
                                    color: "inherit",
                                    "&:hover": { bgcolor: "action.hover" },
                                    "&:not(:last-child)": {
                                        borderBottom: 1,
                                        borderColor: "divider",
                                        borderStyle: "dashed",
                                    },
                                }}
                            >
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="body2" fontWeight={800} noWrap>
                                        {symbol}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {position.quantity} {t("portfolio.units")}
                                    </Typography>
                                </Box>
                                <Box sx={{ textAlign: "right" }}>
                                    <BalanceDisplay
                                        amount={positionValue(position)}
                                        currency={quoteCurrencyView}
                                        isPositive
                                        variant="body2"
                                        showCurrencyCode
                                        keepOriginalCurrency
                                    />
                                    {pnl != null && (
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontWeight: 800,
                                                color: pnl >= 0 ? "success.main" : "error.main",
                                            }}
                                        >
                                            {pnl >= 0 ? "+" : ""}{pnl.toFixed(2)}%
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        );
                    })
                )}
            </Paper>

            {!instrumentEventsLoading && portfolioGuideItems.length > 0 && (
                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: 3,
                        border: "1px solid",
                        borderColor: "divider",
                        overflow: "hidden",
                    }}
                >
                    <Box sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: "divider" }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 850 }}>
                            {t("secondaryPanel.alerts")}
                        </Typography>
                    </Box>
                    {portfolioGuideItems.slice(0, 3).map((item) => (
                        <Box
                            key={item.id}
                            component="button"
                            type="button"
                            onClick={() => handleGuideItemClick(item)}
                            sx={{
                                display: "block",
                                width: "100%",
                                px: 1.5,
                                py: 1,
                                border: "none",
                                background: "none",
                                textAlign: "left",
                                font: "inherit",
                                cursor: "pointer",
                                "&:hover": { bgcolor: "action.hover" },
                                "&:not(:last-child)": {
                                    borderBottom: 1,
                                    borderColor: "divider",
                                },
                            }}
                        >
                            <Typography variant="body2" fontWeight={750}>
                                {item.title}
                            </Typography>
                            {item.body && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                                    {item.body}
                                </Typography>
                            )}
                            {item.actionLabel && (
                                <Typography
                                    variant="caption"
                                    color="primary.main"
                                    sx={{ display: "block", mt: 0.5, fontWeight: 800 }}
                                >
                                    {item.actionLabel}
                                </Typography>
                            )}
                        </Box>
                    ))}
                </Paper>
            )}

            <PanelSectionLink to={investmentsPath} label={t("secondaryPanel.viewPortfolio")} />

            <FloatingActionButton
                label={t("portfolio.addOperation")}
                onClick={() => openOperationDialog(null)}
                disabled={loading || Boolean(error)}
            />

            <AddOperationCard
                onSuccess={refetch}
                addOperation={addOperation}
                positions={positions}
                onCreateWalletTransaction={handleCreateWalletTransaction}
                variant="dialog"
                open={operationDialogOpen}
                onClose={() => {
                    setOperationDialogOpen(false);
                    setOperationInitialValues(null);
                }}
                initialValues={operationInitialValues}
            />
        </Stack>
    );
}
